import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Package, Check, Truck, Home as HomeIcon, X } from "lucide-react";

const STATUS_STEPS = [
  { key: "pending", label: "Pending", icon: Package },
  { key: "confirmed", label: "Confirmed", icon: Check },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: HomeIcon },
];

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get("/orders/track", { params: { number: orderNumber, email } });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No order found with these details");
    }
    setLoading(false);
  };

  const activeStep = order ? STATUS_STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div data-testid="track-order-page">
      <section className="border-b border-[#E3DCD2] bg-[#F3EFEA]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Order tracking</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-tight max-w-3xl">
            Where's my <em className="text-[#D27D60]">order</em>?
          </h1>
          <p className="text-sm text-[#4a4a4a] mt-3 max-w-lg">
            Enter your order number and email — we'll show you exactly where your candles are.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <form
          onSubmit={submit}
          className="p-6 md:p-8 bg-[#F3EFEA]/60 rounded-lg grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
          data-testid="track-order-form"
        >
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Order number</div>
            <input
              required
              placeholder="AVE-XXXXXX-XXXXX"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
              data-testid="track-order-number-input"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Email</div>
            <input
              required
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
              data-testid="track-order-email-input"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="bg-[#1A1A1A] text-[#FDFBF7] px-7 py-3 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="track-order-submit-btn"
          >
            <Search className="w-4 h-4" strokeWidth={1.5} />
            {loading ? "Looking..." : "Track"}
          </button>
        </form>

        {error && (
          <div className="mt-6 p-4 rounded-md border border-[#E3DCD2] text-sm text-[#9BA595] flex items-center gap-2" data-testid="track-order-error">
            <X className="w-4 h-4 text-[#D27D60]" strokeWidth={1.5} />
            {error}
          </div>
        )}

        {order && (
          <div className="mt-10" data-testid="track-order-result">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1">Order</div>
                <div className="font-serif-display text-3xl md:text-4xl">{order.order_number}</div>
              </div>
              <div className="text-sm text-[#9BA595]">
                Placed {new Date(order.created_at).toLocaleDateString()}
              </div>
            </div>

            {order.status !== "cancelled" ? (
              <div className="grid grid-cols-4 gap-2 md:gap-4 mb-10">
                {STATUS_STEPS.map((s, i) => {
                  const done = i <= activeStep;
                  const current = i === activeStep;
                  return (
                    <div key={s.key} className="text-center">
                      <div
                        className={`w-10 h-10 md:w-12 md:h-12 mx-auto rounded-full flex items-center justify-center mb-2 transition-colors ${
                          done ? "bg-[#D27D60] text-[#FDFBF7]" : "bg-[#F3EFEA] text-[#9BA595]"
                        }`}
                      >
                        <s.icon className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} />
                      </div>
                      <div className={`text-[10px] md:text-xs uppercase tracking-widest-plus ${current ? "text-[#D27D60] font-medium" : "text-[#9BA595]"}`}>
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mb-10 p-4 rounded-md border border-[#E3DCD2] bg-[#F3EFEA]/60 text-sm">
                This order was cancelled.
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-[#F3EFEA]/60 rounded-lg">
                <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-4">Items</div>
                <div className="space-y-3">
                  {order.items.map((it, idx) => (
                    <div key={idx} className="flex gap-3 items-center">
                      {it.image && <img src={it.image} alt={it.name} className="w-12 h-12 rounded object-cover" />}
                      <div className="flex-1 text-sm">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-[#9BA595]">₹{it.price} × {it.quantity}</div>
                      </div>
                      <div className="text-sm font-medium">₹{it.price * it.quantity}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 bg-[#F3EFEA]/60 rounded-lg">
                <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-4">Summary</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal.toFixed(2)}</span></div>
                  {order.discount > 0 && (
                    <div className="flex justify-between text-[#D27D60]">
                      <span>Discount</span><span>-₹{order.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : `₹${order.shipping}`}</span></div>
                </div>
                <div className="flex justify-between items-baseline pt-4 mt-4 border-t border-[#E3DCD2]">
                  <span className="text-sm uppercase tracking-widest-plus">Total</span>
                  <span className="font-serif-display text-3xl">₹{order.total.toFixed(2)}</span>
                </div>
                <div className="text-xs text-[#9BA595] mt-3 uppercase tracking-widest-plus">
                  {order.payment_method === "cod" ? "Cash on Delivery" : "WhatsApp Order"}
                </div>
                {order.gift_note && (
                  <div className="mt-5 p-4 rounded-md bg-[#D27D60]/10 border border-[#D27D60]/30" data-testid="track-order-gift-note">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest-plus text-[#D27D60] mb-2">
                      <span className="text-base">✎</span> Your gift note
                    </div>
                    <div className="font-serif-display italic text-base leading-snug">"{order.gift_note}"</div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/shop"
                className="inline-block text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:text-[#D27D60] transition-colors decoration-[#D27D60]/40"
                data-testid="track-order-shop-again"
              >
                Continue shopping
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
